import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { formatCLP, statusBadge, dateLabel } from "../../lib/format";
import { CircleNotch, CaretLeft, CaretRight, CalendarBlank, ListBullets, MapPin, Clock } from "@phosphor-icons/react";

const ACTIVE = ["ADJUDICADO", "TRANSPORTISTA_EN_CAMINO", "EN_ORIGEN", "CARGA_RECOGIDA", "EN_TRASLADO"];
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CarrierAgenda() {
  const nav = useNavigate();
  const [services, setServices] = useState(null);
  const [view, setView] = useState("calendar");
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => {
    api.get("/services/mine")
      .then((r) => setServices((r.data || []).filter((s) => ACTIVE.includes(s.status))))
      .catch(() => setServices([]));
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    const noDate = [];
    (services || []).forEach((s) => {
      const d = s.freight?.requestedDate ? String(s.freight.requestedDate).slice(0, 10) : null;
      if (d) (map[d] = map[d] || []).push(s);
      else noDate.push(s);
    });
    return { map, noDate };
  }, [services]);

  if (!services) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const todayKey = dayKey(new Date());
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="carrier-agenda">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Planificación</span>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Mi agenda</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{services.length} traslado{services.length === 1 ? "" : "s"} adjudicado{services.length === 1 ? "" : "s"} pendiente{services.length === 1 ? "" : "s"} de completar</p>
        </div>
        <div className="flex items-center gap-1 bg-zinc-100 rounded-full p-1" data-testid="agenda-view-toggle">
          <button onClick={() => setView("calendar")} data-testid="view-calendar"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "calendar" ? "bg-white shadow-sm text-[#0F1115]" : "text-zinc-500"}`}>
            <CalendarBlank size={16} /> Calendario
          </button>
          <button onClick={() => setView("list")} data-testid="view-list"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "list" ? "bg-white shadow-sm text-[#0F1115]" : "text-zinc-500"}`}>
            <ListBullets size={16} /> Lista
          </button>
        </div>
      </div>

      {view === "calendar" ? (
        <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="agenda-calendar">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold" data-testid="agenda-month">{MONTHS[month]} {year}</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => { const t = new Date(); setCursor(new Date(t.getFullYear(), t.getMonth(), 1)); }} data-testid="agenda-today"
                className="px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] active:scale-95">Hoy</button>
              <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="agenda-prev"
                className="w-9 h-9 rounded-full border border-zinc-300 flex items-center justify-center text-zinc-600 hover:border-[#0F1115] active:scale-95"><CaretLeft size={16} weight="bold" /></button>
              <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="agenda-next"
                className="w-9 h-9 rounded-full border border-zinc-300 flex items-center justify-center text-zinc-600 hover:border-[#0F1115] active:scale-95"><CaretRight size={16} weight="bold" /></button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1.5">
            {WEEKDAYS.map((w) => <div key={w} className="text-center text-xs font-bold uppercase tracking-widest text-zinc-400 pb-1">{w}</div>)}
            {cells.map((day, i) => {
              if (day === null) return <div key={`e${i}`} />;
              const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const items = byDate.map[key] || [];
              const isToday = key === todayKey;
              return (
                <div key={key} data-testid={`agenda-day-${key}`}
                  className={`min-h-[92px] rounded-xl border p-1.5 flex flex-col ${isToday ? "border-[#FF5A36] bg-[#FFF4F1]" : "border-zinc-200 bg-white"}`}>
                  <div className={`text-xs font-bold mb-1 ${isToday ? "text-[#FF5A36]" : "text-zinc-500"}`}>{day}</div>
                  <div className="flex flex-col gap-1 overflow-hidden">
                    {items.slice(0, 3).map((s) => (
                      <button key={s.id} onClick={() => nav(`/app/service/${s.id}`)} data-testid={`agenda-event-${s.id}`}
                        className="text-left text-[11px] leading-tight bg-[#0F1115] text-white rounded-md px-1.5 py-1 truncate hover:bg-[#1A1D24] active:scale-95">
                        {s.freight?.origin?.city} → {s.freight?.destination?.city}
                      </button>
                    ))}
                    {items.length > 3 && <span className="text-[10px] font-semibold text-zinc-500">+{items.length - 3} más</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {byDate.noDate.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="agenda-nodate">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Sin fecha definida</div>
              <div className="flex flex-wrap gap-2">
                {byDate.noDate.map((s) => (
                  <button key={s.id} onClick={() => nav(`/app/service/${s.id}`)} data-testid={`agenda-event-${s.id}`}
                    className="text-xs font-semibold bg-zinc-100 hover:bg-zinc-200 rounded-full px-3 py-1.5 active:scale-95">
                    {s.freight?.origin?.city} → {s.freight?.destination?.city}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <AgendaList services={services} nav={nav} />
      )}
    </div>
  );
}

function AgendaList({ services, nav }) {
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  const ordered = useMemo(() => {
    const withDate = services.filter((s) => s.freight?.requestedDate)
      .sort((a, b) => String(a.freight.requestedDate).localeCompare(String(b.freight.requestedDate)));
    const noDate = services.filter((s) => !s.freight?.requestedDate);
    return [...withDate, ...noDate];
  }, [services]);

  if (services.length === 0) {
    return <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center text-zinc-500" data-testid="agenda-empty">
      No tienes traslados adjudicados pendientes. Cuando ganes un flete aparecerá aquí en tu agenda.
    </div>;
  }

  const totalPages = Math.max(1, Math.ceil(ordered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const slice = ordered.slice(start, start + PAGE_SIZE);

  const keyOf = (s) => (s.freight?.requestedDate ? String(s.freight.requestedDate).slice(0, 10) : "__none__");
  const grouped = [];
  slice.forEach((s) => {
    const k = keyOf(s);
    let g = grouped[grouped.length - 1];
    if (!g || g.key !== k) { g = { key: k, items: [] }; grouped.push(g); }
    g.items.push(s);
  });

  const Row = ({ s }) => {
    const badge = statusBadge(s.status);
    return (
      <button onClick={() => nav(`/app/service/${s.id}`)} data-testid={`agenda-list-${s.id}`}
        className="w-full flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3 hover:border-[#FF5A36] transition-colors text-left">
        <div className="min-w-0">
          <div className="font-semibold flex items-center gap-1.5"><MapPin size={15} className="text-[#FF5A36]" /> {s.freight?.origin?.city} → {s.freight?.destination?.city}</div>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{s.freight?.category}</span>
            {s.freight?.timeRange && <span className="inline-flex items-center gap-1"><Clock size={12} /> {s.freight.timeRange}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${badge.color}`}>{badge.label}</span>
          <span className="font-display text-lg font-black">{formatCLP(s.agreedPrice)}</span>
        </div>
      </button>
    );
  };

  return (
    <div data-testid="agenda-list">
      <div className="space-y-5">
        {grouped.map((g) => (
          <div key={g.key}>
            <div className="text-sm font-bold text-[#0F1115] mb-2 flex items-center gap-2">
              {g.key === "__none__"
                ? <span className="text-zinc-500">Sin fecha definida</span>
                : <><CalendarBlank size={16} className="text-[#FF5A36]" /> {dateLabel(g.key)}</>}
            </div>
            <div className="space-y-2">{g.items.map((s) => <Row key={s.id} s={s} />)}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4 flex-wrap gap-3" data-testid="agenda-list-pagination">
        <span className="text-sm text-zinc-500" data-testid="agenda-list-range">
          Mostrando {start + 1}–{Math.min(start + PAGE_SIZE, ordered.length)} de {ordered.length}
        </span>
        <div className="flex items-center gap-2">
          <button data-testid="agenda-list-prev" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
            <CaretLeft size={14} weight="bold" /> Anterior
          </button>
          <span className="text-sm font-semibold text-zinc-700" data-testid="agenda-list-page">Página {currentPage} de {totalPages}</span>
          <button data-testid="agenda-list-next" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
            Siguiente <CaretRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
